/// <amd-module name="VSS/Platform/Authentication" />
import { EmptyGuidString } from "VSS/Core/Util/String";
import { IAuthorizationTokenProvider, IVssPageContext, IVssService, Services, VssService } from "./Context";
import { DelegatedAppTokenType, WebSessionToken } from "./Contracts/Authentication";
import { IAuthenticationRestClient } from "./RestClient/Authentication";

/**
 * Interface for a class that can retrieve WebSessionToken authorization tokens to be used in fetch requests.
 */
export interface IWebSessionTokenProvider extends IAuthorizationTokenProvider {
    /**
     * Gets the WebSessionToken to use in a fetch request
     *
     * @param forceRefresh If true, indicates that we should get a new token, if applicable for current provider.
     * @return the web session token information
     */
    getSessionToken(forceRefresh?: boolean): Promise<WebSessionToken>;
}

/**
 * Service for managing authentication-related data such as auth token providers.
 */
export interface IAuthenticationService extends IVssService {
    /**
     * Get the default token provider. Generates a token which can be used across all services targeted at the page's host.
     */
    getDefaultTokenProvider(): IWebSessionTokenProvider;

    /**
     * Gets a named token for the specified id. Generates a token using the server plugin with the given name.
     *
     * @param namedTokenId Name of the INamedWebSessionTokenProvider to invoke
     */
    getNamedTokenProvider(namedTokenId: string): IWebSessionTokenProvider;

    /**
     * Gets a token provider for an extension with the given extension id.
     *
     * @param extensionId Id of the extension to get a token for
     * @param tokenType Type of token to generate
     */
    getExtensionTokenProvider(extensionId: string | undefined, tokenType: DelegatedAppTokenType): IWebSessionTokenProvider;

    /**
     * Gets an auth token provider for the specified bearer token value.
     *
     * @param bearerToken Bearer token value to use in the Authorization header
     */
    getBearerTokenProvider(bearerToken: string): IAuthorizationTokenProvider;
}

export function getBearerTokenAuthHeaderValue(bearerToken: string) {
    return "Bearer " + bearerToken;
}

class TokenProvider implements IWebSessionTokenProvider {
    private pageContext: IVssPageContext;
    private namedTokenId: string | undefined;
    private appId: string | undefined;
    private extensionName: string | undefined;
    private publisherName: string | undefined;
    private tokenType: DelegatedAppTokenType;
    private tokenPromise?: Promise<WebSessionToken>;
    private tokenExpirationTime?: Date;

    public constructor(
        pageContext: IVssPageContext,
        namedTokenId?: string,
        appId = EmptyGuidString,
        extensionId?: string,
        tokenType = DelegatedAppTokenType.Session
    ) {
        this.pageContext = pageContext;
        this.namedTokenId = namedTokenId;
        this.tokenType = tokenType;

        if (extensionId) {
            const dotIndex = extensionId.indexOf(".");
            this.publisherName = extensionId.substr(0, dotIndex);
            this.extensionName = extensionId.substr(dotIndex + 1);
        } else {
            this.appId = appId || EmptyGuidString;
        }
    }

    public getAuthorizationHeader(forceRefresh?: boolean): Promise<string> {
        return this.getSessionToken(forceRefresh).then(token => {
            return getBearerTokenAuthHeaderValue(token.token);
        });
    }

    public getSessionToken(forceRefresh?: boolean): Promise<WebSessionToken> {
        if (!forceRefresh) {
            // Check token expiration time and invalidate if it's almost expired. Check 1 minute before to prevent issues caused by network latency
            if (this.tokenExpirationTime && new Date().getTime() > this.tokenExpirationTime.getTime() - 60000) {
                // Token almost expired, invalidate cache to force a new token
                delete this.tokenExpirationTime;
                delete this.tokenPromise;
            }

            // Attempt to use already-cached token lookup
            if (this.tokenPromise) {
                return this.tokenPromise;
            }
        }

        // Prepare session token object
        const sessionToken = {
            appId: this.appId,
            extensionName: this.extensionName,
            publisherName: this.publisherName,
            force: forceRefresh,
            tokenType: this.tokenType,
            namedTokenId: this.namedTokenId
        } as WebSessionToken;

        const authClient = this.pageContext.getRestClient<IAuthenticationRestClient>("IAuthenticationRestClient");
        this.tokenPromise = authClient.createSessionToken(sessionToken).then(token => {
            this.tokenExpirationTime = token.validTo;
            return token;
        });
        this.tokenPromise.catch(() => delete this.tokenPromise);

        return this.tokenPromise;
    }
}

/**
 * Authorization token provider for a fixed bearer token
 */
export class BearerTokenProvider implements IAuthorizationTokenProvider {
    private header: string;
    constructor(bearerToken: string) {
        this.header = getBearerTokenAuthHeaderValue(bearerToken);
    }
    public getAuthorizationHeader(forceRefresh?: boolean) {
        return Promise.resolve(this.header);
    }
}

class AuthenticationService extends VssService implements IAuthenticationService {
    private _defaultProvider: TokenProvider;
    private _providers: { [name: string]: TokenProvider };

    public getDefaultTokenProvider(): IWebSessionTokenProvider {
        if (!this._defaultProvider) {
            this._defaultProvider = new TokenProvider(this.pageContext);
        }
        return this._defaultProvider;
    }

    public getNamedTokenProvider(namedTokenId: string): IWebSessionTokenProvider {
        return this.getTokenProvider(namedTokenId, undefined, DelegatedAppTokenType.Session);
    }

    public getExtensionTokenProvider(extensionId: string | undefined, tokenType: DelegatedAppTokenType): IWebSessionTokenProvider {
        return this.getTokenProvider(undefined, extensionId, tokenType);
    }

    public getBearerTokenProvider(bearerToken: string): IAuthorizationTokenProvider {
        return {
            getAuthorizationHeader: (forceRefresh?: boolean) => {
                return Promise.resolve(getBearerTokenAuthHeaderValue(bearerToken));
            }
        };
    }

    private getTokenProvider(
        namedTokenId: string | undefined,
        extensionId: string | undefined,
        tokenType: DelegatedAppTokenType
    ): IWebSessionTokenProvider {
        const key = `${namedTokenId}.${extensionId}.${tokenType}`;
        if (!this._providers) {
            this._providers = {};
        }
        let provider = this._providers[key];
        if (!provider) {
            provider = new TokenProvider(this.pageContext, namedTokenId, undefined, extensionId, tokenType);
            this._providers[key] = provider;
        }
        return provider;
    }
}

Services.add("IAuthenticationService", { serviceFactory: AuthenticationService });